import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, ChevronDown, HelpCircle, RefreshCw, Usb } from 'lucide-react'
import { toast } from 'sonner'
import { PageContainer } from '@/components/common/PageContainer'
import { AppButton } from '@/components/rfid/AppButton'
import { RfidAppHeader } from '@/components/rfid/RfidAppHeader'
import { ScanCaptureHint } from '@/components/scanner/ScanCaptureHint'
import { ScannerDevicePickerModal } from '@/components/scanner/ScannerDevicePickerModal'
import { BRAND } from '@/constants/brand'
import { SCANNER_SETUP_GUIDES } from '@/constants/scannerSetup'
import {
  listKnownScanners,
  refreshAvailableScanners,
  upsertKnownScanner,
  type KnownScannerDevice,
} from '@/services/scannerDevices'
import { useLeapBridgeReady } from '@/hooks/useLeapBridgeReady'
import {
  connectHidWedge,
  connectNative,
  disconnect,
  listNativeDevices,
  subscribeKeyActivity,
  subscribeTagReads,
} from '@/services/scannerService'
import {
  isScannerSessionActive,
  useRfidSessionStore,
  type ScannerType,
} from '@/stores/rfidSessionStore'
import { cn } from '@/utils/cn'

const MODELS: ScannerType[] = ['RFD8500', 'RFD40']

export default function ScannerConnectPage() {
  const navigate = useNavigate()
  const scannerConnected = useRfidSessionStore((s) => s.scannerConnected)
  const scannerPending = useRfidSessionStore((s) => s.scannerPending)
  const scannerType = useRfidSessionStore((s) => s.scannerType)
  const deviceConnected = useRfidSessionStore((s) => s.deviceConnected)
  const setHasSkippedScanner = useRfidSessionStore((s) => s.setHasSkippedScanner)

  const sessionActive = isScannerSessionActive({ scannerConnected, scannerPending })
  const native = useLeapBridgeReady()

  const [pickerType, setPickerType] = useState<ScannerType | null>(null)
  const [previewCounts, setPreviewCounts] = useState<Record<ScannerType, number>>({
    RFD8500: 0,
    RFD40: 0,
  })
  const [nativeDevices, setNativeDevices] = useState<
    Array<{ id: string; name: string; type: ScannerType; address?: string }>
  >([])
  const [busy, setBusy] = useState(false)
  const [lastTag, setLastTag] = useState<string | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const [keyProbe, setKeyProbe] = useState<string>('')
  const [keyHits, setKeyHits] = useState(0)

  const reloadCounts = useCallback(async () => {
    if (native) {
      const rfd8500 = await listNativeDevices('RFD8500')
      setNativeDevices(
        rfd8500.map((d) => ({
          id: d.id,
          name: d.name,
          type: d.type,
          address: d.address,
        }))
      )
      setPreviewCounts({
        RFD8500: rfd8500.length,
        RFD40: 1,
      })
      return
    }
    const rfd8500 = await refreshAvailableScanners('RFD8500')
    const rfd40 = await refreshAvailableScanners('RFD40')
    setPreviewCounts({
      RFD8500: rfd8500.length || listKnownScanners('RFD8500').length,
      RFD40: rfd40.length || listKnownScanners('RFD40').length,
    })
  }, [native])

  useEffect(() => {
    void reloadCounts()
  }, [reloadCounts])

  useEffect(() => {
    const name = deviceConnected?.rfid_device?.trim()
    if (!sessionActive || !scannerType || !name) return
    if (name === 'Manual / Mock') return
    upsertKnownScanner({
      id: `session:${scannerType}:${name.toUpperCase()}`,
      name,
      type: scannerType,
      source: native ? 'bluetooth' : 'manual',
    })
    void reloadCounts()
  }, [sessionActive, scannerType, deviceConnected?.rfid_device, reloadCounts, native])

  useEffect(() => {
    if (!sessionActive) {
      setLastTag(null)
      setKeyProbe('')
      setKeyHits(0)
      return
    }
    const unsubTag = subscribeTagReads((tagId) => setLastTag(tagId))
    const unsubKeys = subscribeKeyActivity(({ key }) => {
      setKeyHits((n) => n + 1)
      setKeyProbe((prev) => {
        const next = `${prev}${key === 'Enter' ? '↵' : key === 'Tab' ? '⇥' : key}`
        return next.slice(-48)
      })
    })
    return () => {
      unsubTag()
      unsubKeys()
    }
  }, [sessionActive])

  const onConnectDevice = async (device: KnownScannerDevice) => {
    setBusy(true)
    try {
      upsertKnownScanner(device)
      if (native) {
        await connectNative(device.type, device.name, device.address)
        toast.success(`Connected ${device.name}`)
      } else {
        await connectHidWedge(device.type, device.name)
        toast.message(`Selected ${device.name} — pull the trigger to verify`)
      }
      setPickerType(null)
      await reloadCounts()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not select scanner')
    } finally {
      setBusy(false)
    }
  }

  const onConnectNativeDevice = async (device: {
    id: string
    name: string
    type: ScannerType
    address?: string
  }) => {
    setBusy(true)
    try {
      await connectNative(device.type, device.name, device.address)
      toast.success(`Connected ${device.name}`)
      await reloadCounts()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Connect failed')
    } finally {
      setBusy(false)
    }
  }

  const onConnectRfd40Auto = async () => {
    setBusy(true)
    try {
      await connectNative('RFD40')
      toast.message('Connecting RFD40…')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'RFD40 connect failed')
    } finally {
      setBusy(false)
    }
  }

  const onDisconnect = async () => {
    setBusy(true)
    try {
      await disconnect()
      setLastTag(null)
      toast.message('Scanner cleared')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Disconnect failed')
    } finally {
      setBusy(false)
    }
  }

  const onSkip = () => {
    setHasSkippedScanner(true)
    navigate('/', { replace: true })
  }

  const connectedName = deviceConnected?.rfid_device
  const helpType = pickerType ?? scannerType ?? 'RFD8500'
  const helpGuide = SCANNER_SETUP_GUIDES[helpType]

  const statusTone = scannerConnected
    ? 'live'
    : scannerPending
      ? 'pending'
      : 'off'

  return (
    <PageContainer fluid className="!px-0 !py-0" stack={false}>
      <div className="rfid-web-frame">
        <RfidAppHeader
          title="Connect scanner"
          subtitle={
            native
              ? 'Zebra SDK via RFID Connector — connect, then pull trigger'
              : 'Select a machine, then verify with a real trigger pull'
          }
          onBack={() => navigate('/')}
          right={null}
        />

        <div className="rfid-wizard-body flex flex-1 flex-col gap-3 overflow-y-auto">
          <div
            className="rounded-xl border px-3.5 py-3"
            style={{
              backgroundColor:
                statusTone === 'live'
                  ? '#ECFDF5'
                  : statusTone === 'pending'
                    ? '#FFFBEB'
                    : '#FEF2F2',
              borderColor:
                statusTone === 'live'
                  ? '#86EFAC'
                  : statusTone === 'pending'
                    ? '#FDE68A'
                    : '#FECACA',
            }}
          >
            <div
              className="text-sm font-black"
              style={{
                color:
                  statusTone === 'live'
                    ? '#14532D'
                    : statusTone === 'pending'
                      ? '#92400E'
                      : '#7F1D1D',
              }}
            >
              {statusTone === 'live'
                ? `${scannerType} connected${native ? '' : ' (verified)'}`
                : statusTone === 'pending'
                  ? native
                    ? `${scannerType} connecting…`
                    : `${scannerType} selected — not connected yet`
                  : 'No scanner selected'}
            </div>
            <div className="mt-0.5 break-all font-mono text-[12px] font-bold text-slate-600">
              {sessionActive
                ? connectedName ?? '—'
                : native
                  ? 'Choose RFD8500 or connect RFD40 below'
                  : 'Tap a model to choose a machine code'}
            </div>
            {statusTone === 'pending' && !native ? (
              <p className="m-0 mt-1.5 text-[11px] font-semibold text-amber-900">
                Pull the RFD trigger now. Connection is confirmed only when a tag appears below.
                If nothing arrives, the sled is not linked to this PC as a keyboard (HID) — pair
                it in OS Bluetooth settings, or use the RFID Connector app for SDK Bluetooth.
              </p>
            ) : null}
            {native ? (
              <p className="m-0 mt-1.5 text-[11px] font-semibold text-slate-600">
                Running inside RFID Connector · native Zebra SDK bridge
              </p>
            ) : null}
          </div>

          {!native ? (
            <div className="rounded-xl border border-sky-200 bg-sky-50 px-3.5 py-3">
              <div className="text-[12px] font-extrabold text-sky-950">
                Install LEAP RFID Connector (background utility)
              </div>
              <p className="m-0 mt-1 text-[11.5px] leading-[16px] text-sky-900">
                On Zebra Android devices, install <strong>RFID Connector</strong> and keep the
                &quot;LEAP RFID bridge&quot; notification active. Then open this web app in{' '}
                <strong>Chrome</strong> — scanner commands use{' '}
                <code className="text-[10px]">ws://127.0.0.1:18765</code>. HID keyboard wedge
                remains available below as a fallback.
              </p>
            </div>
          ) : null}

          {native ? (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="text-[12px] font-extrabold text-foreground">RFD8500 (bonded)</div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void reloadCounts()}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-bold text-[var(--leap-brand)]"
                >
                  <RefreshCw size={12} /> Refresh
                </button>
              </div>
              <div className="flex flex-col gap-2">
                {nativeDevices.filter((d) => d.type === 'RFD8500').length === 0 ? (
                  <p className="m-0 text-[11.5px] text-muted-foreground">
                    No bonded RFD8500 found. Pair the sled in Android Bluetooth settings, then
                    refresh.
                  </p>
                ) : (
                  nativeDevices
                    .filter((d) => d.type === 'RFD8500')
                    .map((d) => {
                      const live =
                        scannerConnected &&
                        scannerType === 'RFD8500' &&
                        connectedName === d.name
                      return (
                        <button
                          key={d.id}
                          type="button"
                          disabled={busy}
                          onClick={() => void onConnectNativeDevice(d)}
                          className={cn(
                            'rounded-2xl border-[1.5px] bg-card px-3.5 py-3 text-left',
                            live
                              ? 'border-emerald-500 ring-2 ring-emerald-500/20'
                              : 'border-border hover:border-[var(--leap-brand)]/50'
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-[14px] font-black">{d.name}</span>
                            {live ? (
                              <Check size={15} className="text-emerald-600" strokeWidth={3} />
                            ) : null}
                          </div>
                          <p className="m-0 mt-0.5 text-[11px] font-semibold text-muted-foreground">
                            {live ? 'Connected' : 'Tap to connect via SDK'}
                          </p>
                        </button>
                      )
                    })
                )}
              </div>

              <AppButton
                block
                loading={busy}
                disabled={busy}
                onClick={() => void onConnectRfd40Auto()}
              >
                <span className="inline-flex items-center gap-2">
                  <Usb size={16} /> Connect RFD40 (auto)
                </span>
              </AppButton>
            </>
          ) : (
            <div className="grid gap-2.5 sm:grid-cols-2">
              {MODELS.map((type) => {
                const g = SCANNER_SETUP_GUIDES[type]
                const selectedHere = sessionActive && scannerType === type
                const liveHere = scannerConnected && scannerType === type
                const count = previewCounts[type]
                return (
                  <button
                    key={type}
                    type="button"
                    disabled={busy}
                    onClick={() => setPickerType(type)}
                    className={cn(
                      'rounded-2xl border-[1.5px] bg-card p-3.5 text-left transition-shadow',
                      liveHere
                        ? 'border-emerald-500 shadow-md ring-2 ring-emerald-500/20'
                        : selectedHere
                          ? 'border-amber-400 shadow-md ring-2 ring-amber-400/25'
                          : 'border-border hover:border-[var(--leap-brand)]/50 hover:shadow-md'
                    )}
                  >
                    <div className="flex gap-3">
                      <div className="flex h-[80px] w-[80px] shrink-0 items-center justify-center rounded-xl bg-muted">
                        <img
                          src={g.imageSrc}
                          alt={g.title}
                          className="max-h-[68px] max-w-[68px] object-contain"
                        />
                      </div>
                      <div className="min-w-0 flex-1 self-center">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[15px] font-black text-foreground">{g.title}</span>
                          {liveHere ? (
                            <Check size={15} className="text-emerald-600" strokeWidth={3} />
                          ) : null}
                        </div>
                        <p className="m-0 mt-1 text-[12px] font-semibold leading-4 text-muted-foreground">
                          {liveHere
                            ? `Connected · ${connectedName}`
                            : selectedHere
                              ? `Selected · waiting for scan`
                              : count > 0
                                ? `${count} machine code${count === 1 ? '' : 's'} · tap to select`
                                : 'Tap to select a machine code'}
                        </p>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}

          {sessionActive ? (
            <>
              {!native ? (
                <div className="rounded-xl border border-amber-300 bg-amber-50/80 px-4 py-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-900">
                    Scan target — click this field, then pull the trigger
                  </div>
                  <div className="mt-2">
                    <ScanCaptureHint
                      armed={sessionActive}
                      visible
                      showStatus={false}
                      placeholder="Focus here · pull RFD8500 trigger"
                    />
                  </div>
                  <div className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Live keystrokes from device · {keyHits} hit{keyHits === 1 ? '' : 's'}
                  </div>
                  <div className="mt-1 min-h-[20px] break-all font-mono text-[12px] font-bold text-foreground">
                    {keyProbe || '— none yet —'}
                  </div>
                </div>
              ) : null}

              <div className="rounded-xl border border-border bg-muted/40 px-4 py-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Last tag ·{' '}
                  {scannerConnected
                    ? 'link verified'
                    : native
                      ? 'waiting for connection / tag'
                      : 'pull trigger to verify'}
                </div>
                <div className="mt-1 break-all font-mono text-sm font-semibold text-foreground">
                  {lastTag ?? '— no tag received —'}
                </div>
              </div>

              {scannerConnected ? (
                <AppButton block onClick={() => navigate('/')}>
                  Continue to Home
                </AppButton>
              ) : (
                <AppButton block disabled>
                  {native ? 'Waiting for connection…' : 'Waiting for first tag…'}
                </AppButton>
              )}
              <AppButton
                block
                variant="outline"
                loading={busy}
                onClick={() => void onDisconnect()}
              >
                {native ? 'Disconnect' : 'Clear selection'}
              </AppButton>
            </>
          ) : (
            <button
              type="button"
              onClick={onSkip}
              className="py-2.5 text-center text-[11.5px] font-semibold text-muted-foreground"
            >
              Skip — connect later
            </button>
          )}

          {!native ? (
            <div className="rounded-2xl border border-border bg-card">
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3.5 py-3 text-left"
                onClick={() => setHelpOpen((v) => !v)}
              >
                <HelpCircle size={16} className="shrink-0 text-[var(--leap-brand)]" />
                <span className="min-w-0 flex-1 text-[12px] font-extrabold text-foreground">
                  Why this app cannot “Bluetooth connect” like the mobile app
                </span>
                <ChevronDown
                  size={16}
                  className={cn(
                    'text-muted-foreground transition-transform',
                    helpOpen && 'rotate-180'
                  )}
                />
              </button>
              {helpOpen ? (
                <div className="border-t border-border px-3.5 pb-3.5 pt-2">
                  <p className="m-0 mb-2 text-[11.5px] leading-[16px] text-muted-foreground">
                    The mobile app and RFID Connector open a real Zebra SDK / SPP link. Browsers
                    cannot do that. This web app only receives tags if Windows/macOS already
                    treats the sled as a <strong> keyboard</strong>. Selecting a machine code only
                    tells LEAP which label to show — the green Connected state appears after a tag
                    is actually received.
                  </p>
                  <ol className="m-0 list-none space-y-2 p-0">
                    {helpGuide.steps.slice(0, 3).map((text, i) => (
                      <li key={i} className="flex gap-2.5">
                        <span
                          className="flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full text-[10px] font-extrabold text-muted-foreground"
                          style={{ backgroundColor: 'var(--border)' }}
                        >
                          {i + 1}
                        </span>
                        <p className="m-0 text-[11.5px] leading-[16px] text-foreground">{text}</p>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="mt-auto pt-2 text-center text-[10px] font-bold" style={{ color: BRAND }}>
            RFID Operations · {native ? 'native SDK' : 'verified on first tag'}
          </div>
        </div>
      </div>

      {!native ? (
        <ScannerDevicePickerModal
          open={pickerType != null}
          type={pickerType ?? 'RFD8500'}
          selectedName={
            sessionActive && scannerType === pickerType ? connectedName : null
          }
          verified={scannerConnected && scannerType === pickerType}
          busy={busy}
          onClose={() => setPickerType(null)}
          onConnect={onConnectDevice}
        />
      ) : null}
    </PageContainer>
  )
}
